import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Check, X, Clock, Users, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MarkAttendance() {
  const navigate = useNavigate();
  const { assignmentId } = useParams(); // This is actually class_id from routine
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState({});

  useEffect(() => {
    fetchStudents();
  }, [assignmentId]);

  useEffect(() => {
    if (classInfo) {
      fetchExistingAttendance();
    }
  }, [selectedDate, classInfo]);

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      if (!token) {
        toast.error('Please login again');
        navigate('/staff-portal');
        return;
      }

      const response = await fetch(`${API_URL}/api/staff-portal/my-classes/${assignmentId}/students`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setClassInfo(data.class_info);
        setStudents(data.students || []);
        
        // Initialize attendance with "Present" for all students
        const initialAttendance = {};
        (data.students || []).forEach(s => {
          initialAttendance[s.id] = 'Present';
        });
        setAttendance(initialAttendance);
      } else if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Session expired. Please login again');
        localStorage.removeItem('staffToken');
        navigate('/staff-portal');
      } else if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'You are not assigned to this class');
        navigate('/staff-portal/my-classes');
      } else if (response.status === 404) {
        toast.error('Class not found');
        navigate('/staff-portal/my-classes');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to fetch students');
        navigate('/staff-portal/my-classes');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingAttendance = async () => {
    try {
      const token = localStorage.getItem('staffToken');
      const response = await fetch(
        `${API_URL}/api/staff-portal/attendance/${assignmentId}?date=${selectedDate}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Update attendance state with existing records
        if (data.records && data.records.length > 0) {
          const existingAttendance = { ...attendance };
          data.records.forEach(record => {
            existingAttendance[record.student_id] = record.status;
          });
          setAttendance(existingAttendance);
        }
      }
    } catch (error) {
      console.error('Error fetching existing attendance:', error);
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
  };

  const handleMarkAll = (status) => {
    const newAttendance = {};
    students.forEach(s => {
      newAttendance[s.id] = status;
    });
    setAttendance(newAttendance);
  };

  const handleSave = async () => {
    if (students.length === 0) {
      toast.error('No students to mark attendance');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('staffToken');
      const records = Object.entries(attendance).map(([student_id, status]) => ({
        student_id,
        status
      }));

      const response = await fetch(`${API_URL}/api/staff-portal/student-attendance/mark`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          class_id: assignmentId,
          date: selectedDate,
          records
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Attendance saved! ${result.saved} new, ${result.updated} updated`);
        fetchExistingAttendance();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save attendance');
      }
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Present': return 'bg-green-500 text-white';
      case 'Absent': return 'bg-red-500 text-white';
      case 'Late': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-200';
    }
  };

  const presentCount = Object.values(attendance).filter(s => s === 'Present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'Absent').length;
  const lateCount = Object.values(attendance).filter(s => s === 'Late').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mark-attendance-page">
      {/* Header */}
      <header className="bg-green-700 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/staff-portal/my-classes" className="p-2 hover:bg-green-600 rounded-lg">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex-1">
              <h1 className="text-xl font-bold">Mark Attendance</h1>
              <p className="text-green-200 text-sm">
                {classInfo?.course_name} {classInfo?.semester_name && `• ${classInfo.semester_name}`}
                {classInfo?.subject_name && ` • ${classInfo.subject_name}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Date Selector & Stats */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-gray-400" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  <span className="text-sm">{presentCount} Present</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  <span className="text-sm">{absentCount} Absent</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                  <span className="text-sm">{lateCount} Late</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleMarkAll('Present')} className="text-green-600">
            <Check className="h-4 w-4 mr-1" /> All Present
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleMarkAll('Absent')} className="text-red-600">
            <X className="h-4 w-4 mr-1" /> All Absent
          </Button>
        </div>

        {/* Students List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Students ({students.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No students found in this class</p>
              </div>
            ) : (
              <div className="space-y-2">
                {students.map((student, index) => (
                  <div
                    key={student.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-medium text-sm">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{student.full_name}</p>
                        <p className="text-xs text-gray-500">
                          {student.student_id || student.roll_number}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      {['Present', 'Absent', 'Late'].map(status => (
                        <button
                          key={status}
                          onClick={() => handleStatusChange(student.id, status)}
                          className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                            attendance[student.id] === status
                              ? getStatusColor(status)
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {status === 'Present' && <Check className="inline h-3 w-3 mr-1" />}
                          {status === 'Absent' && <X className="inline h-3 w-3 mr-1" />}
                          {status === 'Late' && <Clock className="inline h-3 w-3 mr-1" />}
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        {students.length > 0 && (
          <div className="sticky bottom-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
              data-testid="save-attendance-btn"
            >
              <Save className="h-5 w-5 mr-2" />
              {saving ? 'Saving...' : 'Save Attendance'}
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
